import { Pencil } from 'lucide-react'
import type React from 'react'
import { cn } from '../lib/utils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../primitives/base/select'
import { Button } from '../primitives/button'
import { Label } from '../primitives/label'
import { ScrollArea } from '../primitives/scroll-area'

interface SelectWithAddNewProps {
  label?: string | React.ReactNode
  options: { label: string; value: string }[]
  value: string
  placeholder: string
  onChange: (value: string) => void
  onCreateNew: () => void
  onEdit?: (value: string) => void
  className?: string
  showCreateNewButton?: boolean
}

export const SelectWithAddNew: React.FC<SelectWithAddNewProps> = ({
  label,
  options,
  value,
  placeholder,
  onChange,
  onCreateNew,
  onEdit,
  className,
  showCreateNewButton = true,
}) => (
  <div className="flex flex-col gap-2">
    {label && <Label>{label}</Label>}
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={className}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="w-[var(--radix-select-trigger-width)]">
        <div className={cn('flex flex-col w-full', showCreateNewButton && 'h-[300px]')}>
          <ScrollArea className="flex-grow">
            {options.map((option) => (
              <div
                key={option.value}
                className="flex items-center justify-between p-2 hover:bg-accent rounded-sm group"
              >
                <SelectItem value={option.value} className="flex-1 border-0 p-0">
                  {option.label}
                </SelectItem>
                {onEdit && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      onEdit(option.value)
                    }}
                    className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0 ml-2"
                    data-testid={`button-edit-${option.value}`}
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                )}
              </div>
            ))}
          </ScrollArea>
          {showCreateNewButton && (
            <div className="border-t mt-2 pt-2 p-2">
              <button type="button" onClick={onCreateNew} data-testid="button-create-new">
                <span className="font-jetbrains text-label uppercase text-secondary">
                  +Create new
                </span>
              </button>
            </div>
          )}
        </div>
      </SelectContent>
    </Select>
  </div>
)
